import React from 'react';
import { Sparkles, Printer, Palette, Monitor, Camera } from 'lucide-react';

import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';

import { Heading } from '../../components';

import css from './ListingPage.module.css';

const ART_TYPE_CONFIG = {
  original_artwork: {
    explanation: "The original hand-painted artwork.",
    icon: Sparkles,
    isPremium: true
  },
  giclee_print: {
    explanation: "High-quality reproduction print of the original artwork.",
    icon: Printer,
    isPremium: false
  },
  mixed_media: {
    explanation: "A print enhanced with original touches by the artist.",
    icon: Palette,
    isPremium: false
  },
  digital_art: {
    explanation: "Created using digital tools.",
    icon: Monitor,
    isPremium: false
  },
  photograph: {
    explanation: "Original photographic work.",
    icon: Camera,
    isPremium: false
  },
};

const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl, hideDimensions = false } = props;

  if (!publicData || !listingFieldConfigs) {
    return null;
  }

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label, rawValue: value })
        : schemaType === 'boolean'
        ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
        : schemaType === 'long'
        ? filteredConfigs.concat({ key, value, label })
        : filteredConfigs;
    }
    return filteredConfigs;
  };

  const existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);

  // Check if dimensions data exists
  const { width, height, dimensionUnit } = publicData;
  const hasDimensions = width && height && dimensionUnit;

  // Format dimensions with both units
  const formatDimensions = () => {
    if (!hasDimensions) return null;

    const widthNum = parseFloat(width);
    const heightNum = parseFloat(height);

    // Convert to the other unit
    const convertedWidth = dimensionUnit === 'inches'
      ? (widthNum * 2.54).toFixed(1)
      : (widthNum / 2.54).toFixed(1);
    const convertedHeight = dimensionUnit === 'inches'
      ? (heightNum * 2.54).toFixed(1)
      : (heightNum / 2.54).toFixed(1);

    // Get unit symbols
    const primarySymbol = dimensionUnit === 'inches' ? '"' : 'cm';
    const secondarySymbol = dimensionUnit === 'inches' ? 'cm' : '"';

    return `${widthNum}${primarySymbol} × ${heightNum}${primarySymbol} (${convertedWidth}${secondarySymbol} × ${convertedHeight}${secondarySymbol})`;
  };

  const dimensionsDisplay = formatDimensions();
  const showDimensions = hasDimensions && !hideDimensions;
  const shouldShowSection = existingListingFields.length > 0 || showDimensions;

  return shouldShowSection ? (
    <section className={css.sectionDetails}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </Heading>
      <ul className={css.details}>
        {existingListingFields.map(detail => {
          if (detail.key === 'type') {
            const config = ART_TYPE_CONFIG[detail.rawValue];
            const explanation = config?.explanation;
            const Icon = config?.icon;
            const isPremium = config?.isPremium;

            return (
              <li key={detail.key} className="col-span-2 mb-4">
                <div className={isPremium
                  ? "bg-amber-50 border border-amber-300 rounded-lg p-3 shadow-sm w-full"
                  : "bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm w-full"}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {Icon && (
                      <span className={isPremium ? "text-amber-700 flex items-center" : "text-gray-700 flex items-center"}>
                        <Icon size={16} className={isPremium ? "drop-shadow-sm" : ""} />
                      </span>
                    )}
                    <span className={isPremium ? "font-bold text-base text-amber-700" : "font-bold text-base text-gray-900"}>{detail.value}</span>
                  </div>
                  {explanation && (
                    <p className="text-sm text-gray-600 m-0">{explanation}</p>
                  )}
                </div>
              </li>
            );
          }
          return (
            <li key={detail.key} className={css.detailsRow}>
              <span className={css.detailLabel}>{detail.label}</span>
              <span>{detail.value}</span>
            </li>
          );
        })}
        {showDimensions && (
          <li className={css.detailsRow}>
            <span className={css.detailLabel}>
              <span className={css.dimensionsLabelWrapper}>
                <FormattedMessage id="ListingPage.dimensionsLabel" />
              </span>
            </span>
            <span className={css.dimensionsValue}>{dimensionsDisplay}</span>
          </li>
        )}
      </ul>
    </section>
  ) : null;
};

export default SectionDetailsMaybe;
